import React, { useRef } from "react";
import { Typography, Box, Chip, Tooltip } from "@mui/material";
import moment from "moment";
import { format } from "date-fns";
import BookItOrCreate from "../bookItOrCreate";
import { useCalendar } from "../calendarContext";

const BookingTypeList = ({ date, showEventDetails, showEventMarker, day, month }) => {
  const { selectedDate, bookingDatas, myBookingTypes } = useCalendar();
  const bookItOrCreateRef = useRef(null);

  // Return null if there are no bookings.
  if (!bookingDatas || bookingDatas.length === 0) return null;

  // Filter bookings to only include those matching the current day.
  const dayBookings = bookingDatas.filter((booking) => {
    const bookingDate = moment(booking.time).startOf("day");
    const comparisonDate = moment(date || selectedDate).startOf("day");
    return bookingDate.isSame(comparisonDate, "day");
  });

  // Group bookings by a key: either "all-day" or the formatted time + booking type name.
  const groupedDayBookings = dayBookings.reduce((acc, booking) => {
    const bookingDate = new Date(booking.time);
    const timeKey = booking.isAllDay ? "all-day" : format(bookingDate, "h:mm a").toLowerCase();
    const groupKey = `${timeKey}-${booking.bookingTypeName}`;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(booking);
    return acc;
  }, {});

  const groupsArray = Object.values(groupedDayBookings);
  // Sort groups: "all-day" groups first, then by time.
  groupsArray.sort((a, b) => {
    if (a[0].isAllDay) return -1;
    if (b[0].isAllDay) return 1;
    return new Date(a[0].time) - new Date(b[0].time);
  });

  return (
    <div style={{ position: "relative", padding: "10px" }}>
      {/* Render event markers if there are any bookings */}
      {dayBookings.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "5px",
            marginBottom: "2px",
          }}
        >
          {/* Single marker for the day */}
          {!showEventDetails && showEventMarker && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <div
                style={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  backgroundColor: "gray",
                }}
              />
            </div>
          )}

          <Typography fontSize="10px" style={{ textAlign: "left", margin: 0 }}>
            {groupsArray.slice(0, 7).map((group, index) => {
              const booking = group[0];
              const bookingDate = new Date(booking.time);
              const bookingTypeName = booking.bookingTypeName;
              // Use "all-day" if applicable, otherwise format the time.
              const timeLabel = booking.isAllDay
                ? "All day: "
                : `${format(bookingDate, "h:mm a").toLowerCase()} - `;
              // Get booking type color from myBookingTypes
              const bookingTypeObj = myBookingTypes?.find(
                (val) => parseInt(val.id) === parseInt(booking.bookingTypeId)
              );
              const color = bookingTypeObj?.color || "gray";

              return (
                <div
                  key={index}
                  onClick={() => bookItOrCreateRef.current("update", group, day, month)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    cursor: "pointer",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  {showEventDetails && (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      {/* Event dot */}
                      <div
                        style={{
                          height: "5px",
                          width: "5px",
                          borderRadius: "2px",
                          background: color,
                        }}
                      />
                      {/* Event details */}
                      <span style={{ fontSize: "0.8rem" }}>
                        {timeLabel}
                        {bookingTypeName}
                      </span>
                      {/* If more than one booking in the group, show count with tooltip */}
                      {group.length > 1 && (
                        <Tooltip title={`Attendees: ${group.length}`} arrow>
                          <Chip
                            label={group.length}
                            size="small"
                            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontSize: "0.75rem" }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Typography>
        </div>
      )}

      {dayBookings.length > 7 && showEventDetails && (
        <Typography fontSize="14px" style={{ marginTop: "1px" }}>
          more ...
        </Typography>
      )}
      <BookItOrCreate triggerDialog={(handler) => (bookItOrCreateRef.current = handler)} />
    </div>
  );
};

export default BookingTypeList;
